import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import TableList from '../../../components/Tools/TableList';
import { formatMoney } from '../../../components/Utils/FormatUtils';
import { FiDownload } from 'react-icons/fi';
import { FaUserTie, FaUserCheck, FaMoneyBillWave, FaMoneyCheckAlt, FaReceipt } from 'react-icons/fa';

// columns will be defined inside to access state

const ExternalPayslipsMonthly = ({ selectedMonth }) => {
    const { t } = useTranslation();
    const [contracts, setContracts] = useState([]);
    const [pagination, setPagination] = useState({ total_count: 0, offset: 0, limit: 10, total_pages: 1, page: 1 });
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterBy, setFilterBy] = useState({});
    const [selectedIds, setSelectedIds] = useState([]);
    const [stats, setStats] = useState(null);

    // Parse selectedMonth "YYYY-MM"
    const [year, month] = selectedMonth.split('-');

    const downloadPdf = async (contractId) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('month', month);
            params.append('year', year);
            const res = await apiService.getElement(`/external_contract/${contractId}/pdf?${params.toString()}`);
            if (res.data && res.data.file && res.data.filename) {
                const link = document.createElement('a');
                link.href = `data:application/pdf;base64,${res.data.file}`;
                link.download = res.data.filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error("Erreur lors du téléchargement PDF:", error);
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => [
        { key: 'employee', label: t('externalPayslipsMonthly.employee'), render: row => row.employee?.name || '' },
        { key: 'contract_type', label: t('externalPayslipsMonthly.contractType'), render: row => <span className="badge bg-[--bg-color] text-[--color-primary]">{row.contract_type}</span> },
        { key: 'worked_days', label: t('externalPayslipsMonthly.workedDays') },
        { key: 'salary', label: t('externalPayslipsMonthly.grossSalary'), render: row => formatMoney(row.salary) },
        { key: 'taxable_prime_amount', label: "Primes Imposables", render: row => row.taxable_prime_amount > 0 ? <span className="font-semibold text-emerald-600">+{formatMoney(row.taxable_prime_amount)}</span> : <span className="text-gray-400 font-italic">-</span> },
        { key: 'taxes', label: t('externalPayslipsMonthly.taxes'), render: row => row.apply_taxes ? formatMoney(row.taxes) : <span className="text-gray-400 font-italic">Non appliqué</span> },
        { key: 'work_accident_amount', label: "Accident du travail", render: row => row.apply_work_accident ? formatMoney(row.work_accident_amount) : <span className="text-gray-400 font-italic">Non appliqué</span> },
        { key: 'net_salary', label: t('externalPayslipsMonthly.netToPay'), render: row => formatMoney(row.net_salary) },
        {
            key: 'actions',
            label: t('externalPayslipsMonthly.actions'),
            render: row => (
                <button
                    onClick={(e) => { e.stopPropagation(); downloadPdf(row.id); }}
                    className="p-2 text-[--color-primary] hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center mx-auto"
                    title={t('externalPayslipsMonthly.downloadPayslip')}
                >
                    <FiDownload size={18} />
                </button>
            )
        }
    ], [month, year, t]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchContracts = useCallback((page = 1, search = '', filter = {}) => {
        setLoading(true);
        const offset = (page - 1) * (pagination.limit || 10);

        // Préparer les paramètres de requête
        const params = new URLSearchParams();
        params.append('month', month);
        params.append('year', year);
        params.append('offset', offset);
        params.append('limit', pagination.limit || 10);
        if (search) params.append('search', search);
        if (filter && Object.keys(filter).length > 0) params.append('filter_by', JSON.stringify(filter));

        apiService.getElement(`/external_contracts/monthly?${params.toString()}`)
            .then(res => {
                setContracts(res.data.contracts || []);
                setPagination({
                    ...res.data.pagination,
                    page: page,
                });
                setStats(res.data.stats || null);
            })
            .finally(() => setLoading(false));
    // eslint-disable-next-line
    }, [month, year, pagination.limit]);

    useEffect(() => {
        fetchContracts(1, searchQuery, filterBy);
    }, [selectedMonth, fetchContracts]);

    // Gestion du changement de page, recherche et filtre
    const handlePageChange = (page, search, filter) => {
        const newSearch = search !== undefined ? search : searchQuery;
        const newFilter = filter !== undefined ? filter : filterBy;
        setSearchQuery(newSearch);
        setFilterBy(newFilter);
        fetchContracts(page, newSearch, newFilter);
    };

    const exportToExcel = async () => {
        const params = new URLSearchParams();
        params.append('month', month);
        params.append('year', year);
        if (searchQuery) params.append('search', searchQuery);
        if (filterBy && Object.keys(filterBy).length > 0) params.append('filter_by', JSON.stringify(filterBy));
        if (selectedIds.length > 0) params.append('ids', JSON.stringify(selectedIds));

        const res = await apiService.getElement(`/external_contracts/monthly/export?${params.toString()}`);
        if (res.data && res.data.file && res.data.filename) {
            const link = document.createElement('a');
            link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${res.data.file}`;
            link.download = res.data.filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    return (
        <div className="">
            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-4">
                    {/* Stagiaires & Prestataires grouped card */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border flex items-center">
                        <div className="bg-[--color-primary] rounded-full p-3 mr-4">
                            <FaUserTie className="text-white" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">{t('externalPayslipsMonthly.internsAndContractors')}</p>
                            <p className="text-xl font-bold text-gray-800">
                                {(stats.stagiaire_count || 0) + (stats.prestataire_count || 0)}
                            </p>
                            <div className="text-xs text-gray-500 mt-1">
                                <span>{t('externalPayslipsMonthly.interns')}: {stats.stagiaire_count || 0}</span>
                                {" | "}
                                <span>{t('externalPayslipsMonthly.contractors')}: {stats.prestataire_count || 0}</span>
                            </div>
                        </div>
                    </div>
                    {/* Total Brut */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border flex items-center">
                        <div className="bg-[--color-primary] rounded-full p-3 mr-4">
                            <FaMoneyBillWave className="text-white" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">{t('externalPayslipsMonthly.totalGross')}</p>
                            <p className="text-xl font-bold text-gray-800">{formatMoney(stats.total_brut)}</p>
                        </div>
                    </div>
                    {/* Total Taxes */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border flex items-center">
                        <div className="bg-[--color-primary] rounded-full p-3 mr-4">
                            <FaReceipt className="text-white" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">{t('externalPayslipsMonthly.totalTaxes')}</p>
                            <p className="text-xl font-bold text-gray-800">{formatMoney(stats.total_taxe)}</p>
                        </div>
                    </div>
                    {/* Total Net */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border flex items-center">
                        <div className="bg-[--color-primary] rounded-full p-3 mr-4">
                            <FaMoneyCheckAlt className="text-white" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">{t('externalPayslipsMonthly.totalNet')}</p>
                            <p className="text-xl font-bold text-gray-800">{formatMoney(stats.total_net)}</p>
                        </div>
                    </div>
                </div>
            )}
            {/* Table */}
            <TableList
                columns={columns}
                data={contracts}
                loading={loading}
                pagination={pagination}
                onPageChange={handlePageChange}
                searchDisplay={true}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                filterBy={filterBy}
                setFilterBy={setFilterBy}
                showFilter={true}
                model="excohsp.external_contract"
                selectable={true}
                onSelectedRowsChange={setSelectedIds}
                selectActions={() => (
                    <button onClick={exportToExcel} className="btn btn-sm btn-outline hover:bg-[--color-primary]" disabled={selectedIds.length === 0}>
                        <FiDownload /> {t('externalPayslipsMonthly.exportToExcel')}
                    </button>
                )}
            />
        </div>
    );
};

export default ExternalPayslipsMonthly;